import { callEvents } from "../protocol/events/call-events";
import * as proto from "../protocol/protocol";
import * as wireEntities from "../protocol/wire-entities";
import { ArtichokeApi } from "../artichoke/artichoke-api";
import { CallReason } from "./call-reason";
import {
  type PeerConnectionStatus,
  type RemoteTrack,
  RTCPool,
} from "../rtc/rtc-pool";
import {
  RecordingRTCPool,
  type RecordingRemoteTrack,
  type RecordingPeerConnectionStatus,
} from "../rtc/recording-rtc-pool";
import { Observable } from "rxjs";
import { filter, take, takeUntil } from "rxjs/operators";
import { LoggerService } from "../logger/logger-service";
import { MediaTrackOptimizer } from "../rtc/media-track-optimizer";
// import { JanusService } from '../janus/janusService';

export class Call implements wireEntities.Call {
  public readonly id: proto.ID;
  public readonly created: proto.Timestamp;
  public readonly creator: proto.ID;
  public readonly direct: boolean;
  // tslint:disable-next-line:readonly-keyword
  public ended?: proto.Timestamp;
  // tslint:disable-next-line:readonly-keyword
  public users: ReadonlyArray<proto.ID>;
  public readonly invitees: ReadonlyArray<proto.ID>;
  public readonly orgId?: proto.ID;
  public readonly recordingEnabled: boolean;
  // public readonly useJanus: boolean;

  public addTracks(tracks: ReadonlyArray<MediaStreamTrack>): void {
    tracks.forEach((track) => this.addTrack(track));
  }

  public addTrack(track: MediaStreamTrack): void {
    this.mediaTrackOptimizer.addContentHint({
      ...track,
      contentHint: "",
    });
    // if (this.useJanus) {
    //   this.janus.addTrack(track);
    // } else {
    this.pool.addTrack(track);
    if (this.recordingPool) {
      this.recordingPool.addTrack(track);
    }
    // }
  }

  public removeTrack(track: MediaStreamTrack): void {
    // if (this.useJanus) {
    //   this.janus.removeTrack(track);
    // } else {
    this.pool.removeTrack(track);
    if (this.recordingPool) {
      this.recordingPool.removeTrack(track);
    }
    // }
  }

  public get remoteTrack$(): Observable<RemoteTrack> {
    return this.pool.remoteTrack$;
  }

  public async replaceTrackByKind(track: MediaStreamTrack): Promise<void> {
    this.mediaTrackOptimizer.addContentHint({
      ...track,
      contentHint: "",
    });

    // if (this.useJanus) {
    //   this.janus.replaceTrackByKind(track);
    // } else {
    await this.pool.replaceTrackByKind(track);
    if (this.recordingPool) {
      await this.recordingPool.replaceTrackByKind(track);
    }
    // }
  }

  public get peerStatus$(): Observable<PeerConnectionStatus> {
    return this.pool.peerStatus$;
  }

  public get recordingRemoteTrack$():
    | Observable<RecordingRemoteTrack>
    | undefined {
    return this.recordingPool?.remoteTrack$;
  }

  public get recordingPeerStatus$():
    | Observable<RecordingPeerConnectionStatus>
    | undefined {
    return this.recordingPool?.peerStatus$;
  }

  public getCachedUsers(): ReadonlyArray<proto.ID> {
    return this.users;
  }

  public async getUsers(): Promise<ReadonlyArray<proto.ID>> {
    return this.artichokeApi.getCallUsers(this.id);
  }

  public async getMessages(): Promise<ReadonlyArray<callEvents.CallEvent>> {
    return this.artichokeApi.getCallHistory(this.id);
  }

  public async answer(tracks: ReadonlyArray<MediaStreamTrack>): Promise<void> {
    this.addTracks(tracks);

    if (this.recordingEnabled) {
      await this.joinRecordingCall(this.invitees[0]!, this.creator);
    }

    return this.artichokeApi.answerCall(this.id);
  }

  public async joinRecordingCall(userId: string, peerId: string): Promise<void> {
    if (!this.recordingPool) {
      this.logger.error("RecordingRTCPool not available for recording call");
      throw new Error("RecordingRTCPool not available for recording call");
    }

    try {
      await this.recordingPool.connectAsOfferSender(userId);
      await this.recordingPool.connectAsAnswerSender(peerId);
      this.logger.debug(
        "Successfully connected to recording call with dual connections"
      );
    } catch (err) {
      this.logger.debug(`Failed to connect to recording call: ${err}`);
      throw err;
    }
  }

  public async reject(reason: CallReason): Promise<void> {
    return this.artichokeApi.rejectCall(this.id, reason);
  }

  public async pull(tracks: ReadonlyArray<MediaStreamTrack>): Promise<void> {
    this.addTracks(tracks);

    return this.artichokeApi.pullCall(this.id);
  }

  public async leave(reason: CallReason): Promise<void> {
    this.pool.destroyAllConnections();

    try {
      await this.artichokeApi.leaveCall(this.id, reason);
    } finally {
      if (this.recordingPool) {
        this.recordingPool.destroyAllConnections();
      }
    }
  }

  public setAudioToggle(enabled: boolean, timestamp: proto.Timestamp): void {
    // if (this.useJanus) {
    //   this.janus.toggleAudio(enabled);
    // }
    return this.artichokeApi.setAudioToggle(this.id, enabled, timestamp);
  }

  public setVideoToggle(
    enabled: boolean,
    timestamp: proto.Timestamp,
    content?: proto.VideoContentType
  ): void {
    return this.artichokeApi.setVideoToggle(
      this.id,
      enabled,
      timestamp,
      content
    );
  }

  public get answered$(): Observable<callEvents.Answered> {
    return this.getCallEvent().pipe(filter(callEvents.Answered.isAnswered));
  }

  public get rejected$(): Observable<callEvents.Rejected> {
    return this.getCallEvent().pipe(filter(callEvents.Rejected.isRejected));
  }

  public get left$(): Observable<callEvents.Left> {
    return this.getCallEvent().pipe(filter(callEvents.Left.isLeft));
  }

  public get offline$(): Observable<callEvents.DeviceOffline> {
    return this.getCallEvent().pipe(
      filter(callEvents.DeviceOffline.isDeviceOffline)
    );
  }

  public get online$(): Observable<callEvents.DeviceOnline> {
    return this.getCallEvent().pipe(
      filter(callEvents.DeviceOnline.isDeviceOnline)
    );
  }

  public get joined$(): Observable<callEvents.Joined> {
    return this.getCallEvent().pipe(filter(callEvents.Joined.isJoined));
  }

  public get activeDevice$(): Observable<callEvents.CallHandledOnDevice> {
    return this.getCallEvent().pipe(
      filter(callEvents.CallHandledOnDevice.isCallHandledOnDevice)
    );
  }

  public get end$(): Observable<callEvents.Ended> {
    return this.getCallEvent().pipe(filter(callEvents.Ended.isEnded));
  }

  public get audioToggle$(): Observable<callEvents.AudioStreamToggled> {
    return this.getCallEvent().pipe(
      filter(callEvents.AudioStreamToggled.isAudioStreamToggled)
    );
  }

  public get videoToggle$(): Observable<callEvents.VideoStreamToggled> {
    return this.getCallEvent().pipe(
      filter(callEvents.VideoStreamToggled.isVideoStreamToggled)
    );
  }

  public get connected$(): Observable<callEvents.Connected> {
    return this.getCallEvent().pipe(filter(callEvents.Connected.isConnected));
  }

  public constructor(
    call: wireEntities.Call,
    private logger: LoggerService,
    private mediaTrackOptimizer: MediaTrackOptimizer,
    protected artichokeApi: ArtichokeApi,
    private pool: RTCPool,
    private recordingPool?: RecordingRTCPool,
    tracks?: ReadonlyArray<MediaStreamTrack>
  ) {
    this.id = call.id;
    this.created = call.created;
    this.ended = call.ended;
    this.creator = call.creator;
    this.users = call.users;
    this.direct = call.direct;
    this.orgId = call.orgId;
    this.invitees = call.invitees;
    this.recordingEnabled = call.recordingEnabled;

    this.end$.pipe(take(1)).subscribe((endEvent) => {
      this.logger.debug(`Call ended, ending and destroying all connections`);
      this.ended = endEvent.timestamp;
      this.pool.destroyAllConnections();
      if (this.recordingPool) {
        this.recordingPool.destroyAllConnections();
      }
    });

    this.activeDevice$.pipe(takeUntil(this.end$)).subscribe(() => {
      this.pool.destroyAllConnections();
      if (this.recordingPool) {
        this.recordingPool.destroyAllConnections();
      }
    });

    this.joined$.pipe(takeUntil(this.end$)).subscribe((joined) => {
      console.warn(`User ${joined.authorId} joined, creating rtc connection}`);
      this.users = [...this.users, joined.authorId];
      if (!this.recordingEnabled) {
        this.logger.debug(
          `Joined event for ${joined.authorId}; invoking pool.connect (recordingEnabled=${this.recordingEnabled})`
        );
        this.pool.connect(joined.authorId).then(
          () => this.logger.debug("Connected"),
          (err) => this.logger.error(err as string)
        );
      }
    });

    this.left$.pipe(takeUntil(this.end$)).subscribe((left) => {
      this.logger.debug(
        `User ${left.authorId} left, removing and destroying connection`
      );
      this.users = this.users.filter((u) => u !== left.authorId);
      if (this.recordingEnabled) {
        this.recordingPool?.destroyAllConnections();
      } else {
        this.pool.destroyConnection(left.authorId);
      }
    });

    if (tracks) {
      this.addTracks(tracks);
    }

    this.establishRTCWithOldUsers();

    logger.debug(`Created`);
  }

  private getCallEvent(): Observable<callEvents.CallEvent> {
    return this.artichokeApi.domainEvent$.pipe(
      filter(callEvents.CallEvent.isCallEvent),
      filter((ev) => ev.callId === this.id)
    );
  }

  private establishRTCWithOldUsers() {
    if (!this.recordingEnabled) {
      this.users.filter((id) => id !== this.creator).forEach((id) => this.pool.connect(id));
    }
  }

}
